import apiClient from '../../../services/api/client';
import type {
  AmlClient, AmlCustomer, AmlTransaction, AmlLocation,
  AmlMe, AmlUpload, AmlPivotRow, AmlUser,
  ListResponse, AmlSection,
  XlsxParseResponse, XlsxCommitRequest, XlsxCommitResponse,
  XlsxImportDraft, XlsxImportDraftMeta, XlsxImportDraftReviewState,
  SanctionsListStatus, SanctionsListType, ScreeningRun, ScreeningResult,
  ScreeningResultType, ScreeningReviewStatus, CustomerScreening,
} from '../types/aml';

const BASE = '/api/aml';

export const amlApi = {
  // Me
  getMe: () => apiClient.get<AmlMe>(`${BASE}/me`).then(r => r.data),

  // Clients
  listClients: () => apiClient.get<AmlClient[]>(`${BASE}/clients`).then(r => r.data),
  createClient: (data: { name: string; address?: string | null; phone?: string | null }) =>
    apiClient.post<AmlClient>(`${BASE}/clients`, data).then(r => r.data),
  getClient: (id: string) => apiClient.get<AmlClient>(`${BASE}/clients/${id}`).then(r => r.data),
  updateClient: (id: string, data: Partial<AmlClient>) =>
    apiClient.patch<AmlClient>(`${BASE}/clients/${id}`, data).then(r => r.data),
  deleteClient: (id: string) => apiClient.delete(`${BASE}/clients/${id}`),
  uploadClientLogo: (clientId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<AmlClient>(
      `${BASE}/clients/${clientId}/logo`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(r => r.data);
  },
  deleteClientLogo: (clientId: string) =>
    apiClient.patch<AmlClient>(`${BASE}/clients/${clientId}`, { image_name: null }).then(r => r.data),
  getClientLogoUrl: (imageName: string) => `${BASE}/client-logos/${encodeURIComponent(imageName)}`,

  // Customers
  listCustomers: (params?: { page?: number; page_size?: number; search?: string; order_by?: string; order_dir?: string }) =>
    apiClient.get<ListResponse<AmlCustomer>>(`${BASE}/customers`, { params }).then(r => r.data),
  createCustomer: (data: Partial<AmlCustomer> & { locations?: { id?: string; name: string }[] }) =>
    apiClient.post<AmlCustomer>(`${BASE}/customers`, data).then(r => r.data),
  getCustomer: (id: string) => apiClient.get<AmlCustomer>(`${BASE}/customers/${id}`).then(r => r.data),
  updateCustomer: (id: string, data: Partial<AmlCustomer> & { locations?: { id?: string; name: string }[] }) =>
    apiClient.patch<AmlCustomer>(`${BASE}/customers/${id}`, data).then(r => r.data),
  updateCustomerFull: (id: string, data: Partial<AmlCustomer> & { locations?: { id?: string; name: string }[] }) =>
    apiClient.put<AmlCustomer>(`${BASE}/customers/${id}`, data).then(r => r.data),
  deleteCustomer: (id: string) => apiClient.delete(`${BASE}/customers/${id}`),
  getCustomerLocationUsage: (id: string) =>
    apiClient.get<{ location_ids: string[] }>(`${BASE}/customers/${id}/location-usage`).then(r => r.data),
  downloadKyc: (id: string) =>
    apiClient.get(`${BASE}/customers/${id}/download-kyc`, { responseType: 'blob' }).then(r => r.data),

  // Locations
  listLocations: (params?: { customer_id?: string }) =>
    apiClient.get<AmlLocation[]>(`${BASE}/locations`, { params }).then(r => r.data),
  createLocation: (data: { aml_customer_id: string; name: string }) =>
    apiClient.post<AmlLocation>(`${BASE}/locations`, data).then(r => r.data),
  updateLocation: (id: string, data: { name?: string }) =>
    apiClient.patch<AmlLocation>(`${BASE}/locations/${id}`, data).then(r => r.data),
  deleteLocation: (id: string) => apiClient.delete(`${BASE}/locations/${id}`),

  // Transactions
  listTransactions: (params?: {
    page?: number; page_size?: number;
    customer_id?: string; location_id?: string;
    date_from?: string; date_to?: string;
    order_by?: string; order_dir?: string;
  }) =>
    apiClient.get<ListResponse<AmlTransaction>>(`${BASE}/transactions`, { params }).then(r => r.data),
  getTransactionReport: (params?: {
    customer_id?: string[]; location_id?: string[];
    date_from?: string; date_to?: string;
    inv_date_from?: string; inv_date_to?: string;
  }) =>
    apiClient.get<AmlTransaction[]>(`${BASE}/transactions/report`, {
      params,
      paramsSerializer: (p) => {
        const parts: string[] = [];
        for (const [k, v] of Object.entries(p)) {
          if (v == null) continue;
          if (Array.isArray(v)) v.forEach((item) => parts.push(`${k}=${encodeURIComponent(item)}`));
          else parts.push(`${k}=${encodeURIComponent(v as string)}`);
        }
        return parts.join('&');
      },
    }).then(r => r.data),
  createTransaction: (data: Partial<AmlTransaction>) =>
    apiClient.post<AmlTransaction>(`${BASE}/transactions`, data).then(r => r.data),
  updateTransaction: (id: string, data: Partial<AmlTransaction>) =>
    apiClient.patch<AmlTransaction>(`${BASE}/transactions/${id}`, data).then(r => r.data),
  deleteTransaction: (id: string) => apiClient.delete(`${BASE}/transactions/${id}`),
  bulkDeleteTransactions: (ids: string[]) =>
    apiClient.post<{ deleted: number }>(`${BASE}/transactions/bulk-delete`, { ids }).then(r => r.data),

  importTransactions: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ imported: number; errors: string[] }>(
      `${BASE}/transactions/import`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(r => r.data);
  },

  downloadImportTemplate: () =>
    apiClient.get(`${BASE}/transactions/import-xlsx/sample`, { responseType: 'blob' }).then(r => r.data),

  parseXlsxImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<XlsxParseResponse>(
      `${BASE}/transactions/import-xlsx/parse`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(r => r.data);
  },

  commitXlsxImport: (data: XlsxCommitRequest) =>
    apiClient.post<XlsxCommitResponse>(
      `${BASE}/transactions/import-xlsx/commit`, data
    ).then(r => r.data),

  getImportDraftMeta: () =>
    apiClient.get<XlsxImportDraftMeta>(`${BASE}/transactions/import-xlsx/draft/meta`).then(r => r.data),

  getImportDraft: () =>
    apiClient.get<XlsxImportDraft>(`${BASE}/transactions/import-xlsx/draft`).then(r => r.data),

  putImportDraft: (data: {
    file_name?: string | null;
    parse_data: XlsxParseResponse;
    review_state: XlsxImportDraftReviewState;
  }) =>
    apiClient.put<XlsxImportDraftMeta>(`${BASE}/transactions/import-xlsx/draft`, data).then(r => r.data),

  patchImportDraft: (data: {
    file_name?: string | null;
    review_state: XlsxImportDraftReviewState;
  }) =>
    apiClient.patch<XlsxImportDraftMeta>(`${BASE}/transactions/import-xlsx/draft`, data).then(r => r.data),

  deleteImportDraft: () =>
    apiClient.delete(`${BASE}/transactions/import-xlsx/draft`),

  downloadTransactionReport: (data: {
    customer_ids?: string[]; location_ids?: string[];
    date_from?: string; date_to?: string;
    inv_date_from?: string; inv_date_to?: string;
  }) =>
    apiClient.post(`${BASE}/transactions/report-download`, data, { responseType: 'blob' }).then(r => r.data),

  // Pivot report
  getPivotReport: (params?: { date_from?: string; date_to?: string; by_client?: string }) =>
    apiClient.get<AmlPivotRow[]>(`${BASE}/pivot-report`, { params }).then(r => r.data),
  downloadPivotPdf: (data: { date_from?: string; date_to?: string; by_client?: string }) =>
    apiClient.post(`${BASE}/transactions/report-pivot-download`, data, { responseType: 'blob' }).then(r => r.data),
  downloadPivotExcel: (data: { date_from?: string; date_to?: string; by_client?: string }) =>
    apiClient.post(`${BASE}/transactions/report-pivot-download-excel`, data, { responseType: 'blob' }).then(r => r.data),

  // Uploads
  uploadFile: (file: File, fileType: string = 'TRANSACTION_IMPORT') => {
    const form = new FormData();
    form.append('file', file);
    form.append('file_type', fileType);
    return apiClient.post<AmlUpload>(
      `${BASE}/uploads`, form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    ).then(r => r.data);
  },
  getUpload: (id: string) => apiClient.get<AmlUpload>(`${BASE}/uploads/${id}`).then(r => r.data),

  // Users
  listAmlUsers: () => apiClient.get<AmlUser[]>(`${BASE}/users`).then(r => r.data),
  getAmlUser: (id: string) => apiClient.get<AmlUser>(`${BASE}/users/${id}`).then(r => r.data),
  createAmlUser: (data: {
    email: string; first_name?: string; last_name?: string;
    password?: string; system_role?: string; aml_client_id?: string; is_active?: boolean;
  }) => apiClient.post<AmlUser>(`${BASE}/users`, data).then(r => r.data),
  updateAmlUser: (id: string, data: {
    first_name?: string; last_name?: string; password?: string;
    system_role?: string; aml_client_id?: string; is_active?: boolean;
  }) => apiClient.patch<AmlUser>(`${BASE}/users/${id}`, data).then(r => r.data),
  updateUserAmlRole: (userId: string, amlRole: string | null) =>
    apiClient.patch(`${BASE}/users/${userId}/role`, { aml_role: amlRole }).then(r => r.data),
  deleteAmlUser: (id: string) => apiClient.delete(`${BASE}/users/${id}`),
  requestPasswordReset: (userId: string) =>
    apiClient.post(`${BASE}/users/${userId}/password-reset`).then(r => r.data),
  resetUserPassword: (userId: string, data: { password: string; confirm_password: string }) =>
    apiClient.post(`${BASE}/users/${userId}/set-password`, data).then(r => r.data),
  impersonateUser: (userId: string) =>
    apiClient.post<{ access_token: string; id_token: string; refresh_token: string; email: string }>(
      `${BASE}/users/${userId}/impersonate`
    ).then(r => r.data),

  // OFAC sanctions screening
  getSanctionsLists: () =>
    apiClient.get<{ lists: SanctionsListStatus[] }>(`${BASE}/sanctions/lists`).then(r => r.data),
  refreshSanctionsLists: (listType?: SanctionsListType) =>
    apiClient.post<{ status: string; list_type: string }>(
      `${BASE}/sanctions/lists/refresh`, { list_type: listType ?? null }
    ).then(r => r.data),

  startScreeningRun: (threshold = 80, customerIds?: string[]) =>
    apiClient.post<ScreeningRun>(`${BASE}/sanctions/screenings`, {
      threshold,
      customer_ids: customerIds?.length ? customerIds : undefined,
    }).then(r => r.data),
  listScreeningRuns: (params?: { page?: number; page_size?: number }) =>
    apiClient.get<ListResponse<ScreeningRun>>(`${BASE}/sanctions/screenings`, { params }).then(r => r.data),
  getScreeningRun: (runId: string) =>
    apiClient.get<ScreeningRun>(`${BASE}/sanctions/screenings/${runId}`).then(r => r.data),
  listScreeningResults: (runId: string, params?: {
    page?: number; page_size?: number;
    result?: ScreeningResultType; review_status?: ScreeningReviewStatus; search?: string;
  }) =>
    apiClient.get<ListResponse<ScreeningResult>>(
      `${BASE}/sanctions/screenings/${runId}/results`, { params }
    ).then(r => r.data),
  reviewScreeningResult: (resultId: string, data: {
    review_status: ScreeningReviewStatus; review_notes?: string | null;
  }) =>
    apiClient.patch<ScreeningResult>(
      `${BASE}/sanctions/screenings/results/${resultId}`, data
    ).then(r => r.data),
  downloadScreeningReport: (runId: string, format: 'pdf' | 'xlsx') =>
    apiClient.get(`${BASE}/sanctions/screenings/${runId}/download`, {
      params: { format }, responseType: 'blob',
    }).then(r => r.data),
  getCustomerScreening: (customerId: string) =>
    apiClient.get<CustomerScreening>(`${BASE}/customers/${customerId}/screening`).then(r => r.data),
};
